/* UnifiedSearch — single search overlay that surfaces results across all
 * three ZHELTA divisions (Systems, Services e-store, Academy) in one query.
 *
 * Triggered by clicking the search icon in the main Header. Renders as a
 * fixed-position glass overlay with a debounced input, 3 grouped result
 * sections, and keyboard-friendly navigation (Esc closes).
 *
 * Each result links to the correct native page so visitors stay inside the
 * monorepo — no external bounce. */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, ArrowUpRight, Cpu, ShoppingBag, GraduationCap, TrendingUp } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const KIND_META = {
  systems_service: { label: 'ZHELTA Systems', Icon: Cpu, accent: '#5b8def' },
  services_product: { label: 'ZHELTA Services', Icon: ShoppingBag, accent: '#c9a227' },
  academy_program: { label: 'ZHELTA Academy', Icon: GraduationCap, accent: '#a875ff' },
};

export default function UnifiedSearch({ open, onClose }) {
  const [q, setQ] = useState('');
  const [data, setData] = useState({ systems: [], services: [], academy: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [trending, setTrending] = useState([]);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const nav = useNavigate();
  const { format } = useCurrency();

  // Focus the input when the overlay opens, AND fetch trending suggestions
  // so the idle state shows "what's hot" instead of static placeholder text.
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      // Trending — public endpoint, cheap query, fire on every open so the
      // suggestions stay fresh across sessions without aggressive caching.
      fetch(`${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api/search/trending?days=7&limit=5`)
        .then((r) => r.json())
        .then((d) => setTrending(d?.rows || []))
        .catch(() => setTrending([]));
    } else {
      // Reset on close so reopening starts fresh
      setQ('');
      setData({ systems: [], services: [], academy: [], total: 0 });
    }
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Debounced fetch
  useEffect(() => {
    if (!open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const needle = q.trim();
    if (!needle) {
      setData({ systems: [], services: [], academy: [], total: 0 });
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api/search?q=${encodeURIComponent(needle)}&limit=6`);
        const json = await r.json();
        setData({
          systems: json?.systems || [],
          services: json?.services || [],
          academy: json?.academy || [],
          total: json?.total || 0,
        });
      } catch {
        setData({ systems: [], services: [], academy: [], total: 0 });
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, open]);

  const goto = (url, kind, slug) => {
    // Fire-and-forget click log when this is an actual search-result click
    // (kind+slug supplied). Browse fallbacks pass neither and skip the log.
    if (kind && slug) {
      try {
        fetch(`${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api/search/click`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ q: q.trim(), kind, slug }),
          keepalive: true,
        }).catch(() => {});
      } catch { /* ignore */ }
    }
    onClose();
    nav(url);
  };

  if (!open) return null;

  const Section = ({ title, kind, rows }) => {
    if (!rows.length) return null;
    const meta = KIND_META[kind];
    const Icon = meta.Icon;
    return (
      <div className="px-2 py-3" data-testid={`unified-search-section-${kind}`}>
        <div className="flex items-center gap-2 px-3 mb-2">
          <Icon className="w-3.5 h-3.5" style={{ color: meta.accent }} />
          <span className="text-[10.5px] font-bold uppercase tracking-[0.22em]" style={{ color: meta.accent }}>{title}</span>
          <span className="text-[10.5px] text-white/35">· {rows.length}</span>
        </div>
        <ul className="flex flex-col">
          {rows.map((r) => (
            <li key={`${kind}-${r.slug}`}>
              <button
                type="button"
                onClick={() => goto(r.url, kind, r.slug)}
                data-testid={`unified-search-result-${kind}-${r.slug}`}
                className="w-full text-left px-3 py-2.5 rounded-md hover:bg-white/5 flex items-center gap-3 transition-colors group"
              >
                {kind === 'services_product' && r.image ? (
                  <span className="h-10 w-10 shrink-0 rounded-md overflow-hidden bg-white/5 grid place-items-center">
                    <img src={r.image} alt="" className="h-10 w-10 object-cover" loading="lazy" />
                  </span>
                ) : (
                  <span className="h-10 w-10 shrink-0 rounded-md grid place-items-center" style={{ background: `${meta.accent}1a`, color: meta.accent }}>
                    <Icon className="w-4 h-4" />
                  </span>
                )}
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-semibold text-white truncate">{r.name}</span>
                  {(r.tagline || r.category) && (
                    <span className="block text-[12px] text-white/55 truncate">
                      {r.category ? <span className="uppercase tracking-wider text-[10px] mr-1.5">{r.category}</span> : null}
                      {r.tagline}
                    </span>
                  )}
                </span>
                {kind === 'services_product' && typeof r.price === 'number' && r.price > 0 && (
                  <span className="text-[12.5px] font-bold text-white/85 shrink-0 mr-1">{format(r.price)}</span>
                )}
                <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-[#f5a623] shrink-0 transition-colors" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const empty = !loading && q.trim() && data.total === 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4"
      style={{ background: 'rgba(8,8,10,0.72)', backdropFilter: 'blur(8px)' }}
      data-testid="unified-search-overlay"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[640px] rounded-2xl overflow-hidden shadow-[0_25px_60px_-15px_rgba(0,0,0,0.6)]"
        style={{ background: 'linear-gradient(180deg,#1a1a1d 0%,#0e0e10 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        {/* Search input row */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
          <Search className="w-5 h-5 text-[#f5a623]" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search Systems · Services · Academy…"
            className="flex-1 bg-transparent outline-none text-white placeholder:text-white/30 text-[15px]"
            data-testid="unified-search-input"
          />
          {loading && <Loader2 className="w-4 h-4 text-white/40 animate-spin" />}
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="unified-search-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {!q.trim() && (
            <div className="px-5 py-7" data-testid="unified-search-idle">
              {trending.length > 0 ? (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-3.5 h-3.5 text-[#f5a623]" />
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-[#f5a623]">
                      Trending searches · last 7 days
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="unified-search-trending">
                    {trending.map((t, i) => (
                      <button
                        key={t.q_lower}
                        type="button"
                        onClick={() => setQ(t.q)}
                        data-testid={`unified-search-trending-${t.q_lower}`}
                        className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-[#c9a227]/20 hover:border-[#c9a227]/60 transition-colors text-[12px] text-white/80 hover:text-white"
                      >
                        <span className="text-[10px] font-mono text-white/30 group-hover:text-[#f5a623]">{i + 1}</span>
                        <span className="font-medium">{t.q}</span>
                        <span className="text-[10px] text-white/30">·{t.unique_visitors}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-[10.5px] text-white/30 mt-4">Click any chip to run that search, or type your own.</p>
                </>
              ) : (
                <div className="text-center">
                  <p className="text-[13px] text-white/45 mb-1">One search across all three divisions.</p>
                  <p className="text-[11.5px] text-white/30">Try <span className="text-white/55">"cybersecurity"</span>, <span className="text-white/55">"macbook"</span>, or <span className="text-white/55">"certification"</span>.</p>
                </div>
              )}
            </div>
          )}
          {empty && (
            <div className="px-5 py-10 text-center" data-testid="unified-search-empty">
              <p className="text-[13.5px] text-white/55 mb-1">No matches for <span className="text-white">"{q}"</span></p>
              <p className="text-[11.5px] text-white/30">Try a broader term or browse <button className="underline hover:text-[#f5a623]" onClick={() => goto('/services')}>Services</button> · <button className="underline hover:text-[#f5a623]" onClick={() => goto('/systems/solutions')}>Systems</button> · <button className="underline hover:text-[#f5a623]" onClick={() => goto('/academy/programs')}>Academy</button>.</p>
            </div>
          )}
          {data.total > 0 && (
            <div className="py-1">
              <Section title="Systems" kind="systems_service" rows={data.systems} />
              <Section title="Services" kind="services_product" rows={data.services} />
              <Section title="Academy" kind="academy_program" rows={data.academy} />
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-5 py-2.5 border-t border-white/5 bg-black/30 text-[10.5px] text-white/35 flex items-center justify-between">
          <span><kbd className="px-1.5 py-0.5 rounded bg-white/5 text-white/55 font-mono text-[10px]">Esc</kbd> to close</span>
          {data.total > 0 && <span>{data.total} result{data.total === 1 ? '' : 's'}</span>}
        </div>
      </div>
    </div>
  );
}
